import { useState, useRef, useCallback } from 'react';
import { toast } from "@/hooks/use-toast";

interface UseRateLimitConfig {
  maxAttempts: number;
  timeWindow: number;
  cooldownPeriod: number;
}

export const useRateLimit = (config: UseRateLimitConfig = {
  maxAttempts: 5,
  timeWindow: 60000,
  cooldownPeriod: 30000
}) => {
  const [isLimited, setIsLimited] = useState(false);
  const attemptTimestamps = useRef<number[]>([]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const timeWindow = config.timeWindow;
    
    attemptTimestamps.current = attemptTimestamps.current.filter(
      timestamp => now - timestamp < timeWindow
    );

    if (isLimited) {
      toast({
        variant: "destructive",
        title: "Too many save attempts",
        description: "Please wait before trying to save again.",
      });
      return false;
    }

    if (attemptTimestamps.current.length >= config.maxAttempts) {
      setIsLimited(true);
      
      setTimeout(() => {
        setIsLimited(false);
        attemptTimestamps.current = [];
      }, config.cooldownPeriod);

      toast({
        variant: "destructive",
        title: "Save limit reached",
        description: `Please wait ${config.cooldownPeriod / 1000} seconds before saving again.`,
      });
      
      return false;
    }

    attemptTimestamps.current.push(now);
    return true;
  }, [config.maxAttempts, config.timeWindow, config.cooldownPeriod, isLimited]);

  return {
    checkRateLimit,
    isLimited
  };
};